import { useEffect, useState } from "react";
import { ApiError, PayrollValidationError, calculatePayrollRun } from "@/api/client";
import type { PaycheckResponse, PayrollEmployee, PayrollRunEmployeeResult } from "@/api/types";
import { PAY_PERIODS_PER_YEAR, TAX_YEAR } from "@/pages/payroll-fixtures";
import { describeReadFailure } from "@/lib/apiFailure";

export type EmployeePaycheckStatus = "loading" | "ready" | "error";

export interface EmployeePaycheck {
  employee: PayrollEmployee;
  status: EmployeePaycheckStatus;
  paycheck: PaycheckResponse | null;
  error: string | null;
}

export interface PayrollRunTotals {
  gross: number;
  /** SS + Medicare + Additional Medicare, employee side. */
  ficaMedicare: number;
  /** Federal + state withholding. */
  withholding: number;
  netPay: number;
}

export type PayrollRunStatus = "loading" | "ready";

export interface UsePayrollRunResult {
  rows: EmployeePaycheck[];
  status: PayrollRunStatus;
  /**
   * Totals exactly as returned by POST /api/payroll/run — never summed on the
   * client. Null until the run resolves, and null if the request itself
   * failed (no employee could be computed). The server already excludes any
   * failed row from this figure, so it is a real total for the employees the
   * engine computed, not the whole run, when `erroredCount > 0`.
   */
  totals: PayrollRunTotals | null;
  readyCount: number;
  erroredCount: number;
}

/** Turns a row's field->messages error map into one human-readable string, the
 *  same join PayrollValidationError uses for a request-level failure -- so a
 *  per-employee failure and a whole-run failure read the same way on screen. */
function messageFor(errors: Record<string, string[]> | null): string {
  if (!errors) return "Could not reach the payroll engine.";
  const joined = Object.values(errors).flat().join(" ");
  return joined || "The payroll engine rejected this request.";
}

function toEmployeePaycheck(
  employee: PayrollEmployee,
  result: PayrollRunEmployeeResult | undefined,
): EmployeePaycheck {
  if (!result) {
    // The engine echoes back exactly the employeeIds it was given; a missing
    // row would mean the response silently dropped a roster entry, which is
    // itself the "corrupted run" failure mode this whole change guards
    // against -- surfaced honestly rather than rendered as a false success.
    return {
      employee,
      status: "error",
      paycheck: null,
      error: "The payroll engine did not return a result for this employee.",
    };
  }

  return result.success
    ? { employee, status: "ready", paycheck: result.paycheck, error: null }
    : { employee, status: "error", paycheck: null, error: messageFor(result.errors) };
}

/**
 * Fires ONE POST /api/payroll/run for the whole roster (replacing the old shape
 * of one POST /api/payroll/paycheck per employee -- 12 round trips on every
 * Payroll screen load) and renders the real, engine-computed results per row,
 * plus the run totals the SAME response already carries. A bad employee in the
 * roster (negative gross, bad pay-period count, unconfigured tax year) surfaces
 * as that row's own error -- it does not fail the whole run, corrupt any other
 * row, or get folded into the totals below.
 *
 * @param employees The roster from usePayrollRoster, or `null` while it is still
 * loading / failed to load. `null` and `[]` are deliberately different: `null` means
 * NOT KNOWN YET, and this hook stays in `loading` without calling the engine; `[]` is a
 * real answer (nobody on this entity's payroll) and is also not sent, because a run of
 * an empty roster is a request-level 400 and there is nothing to compute. Collapsing the
 * two would let the screen render a confident $0.00 run for a farm whose roster simply
 * had not arrived.
 */
export function usePayrollRun(employees: PayrollEmployee[] | null): UsePayrollRunResult {
  const [rows, setRows] = useState<EmployeePaycheck[]>([]);
  const [totals, setTotals] = useState<PayrollRunTotals | null>(null);

  useEffect(() => {
    if (employees === null || employees.length === 0) {
      setRows([]);
      setTotals(null);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    // Placeholder rows the moment a roster arrives, so the screen shows who is being
    // computed rather than an empty list that fills in all at once.
    setRows(
      employees.map((employee) => ({
        employee,
        status: "loading" as const,
        paycheck: null,
        error: null,
      })),
    );

    calculatePayrollRun(
      {
        employees: employees.map((employee) => ({
          employeeId: employee.id,
          grossWages: employee.grossWages,
          payPeriodsPerYear: PAY_PERIODS_PER_YEAR,
          taxYear: TAX_YEAR,
          filingStatus: "Single",
          // The roster's H-2A badge IS the FICA determination (settled federal rule --
          // see payroll-fixtures.ts and FicaMedicareInput.FicaExempt for the IRS
          // citation), unlike ficaExemptByLaw's deliberate non-use on the MN Paid Leave
          // call (usePaidLeave.ts), where the same question is legally open.
          ficaExempt: employee.ficaExemptByLaw,
        })),
      },
      controller.signal,
    )
      .then((run) => {
        if (cancelled) return;
        const byId = new Map(run.employees.map((r) => [r.employeeId, r]));
        setRows(employees.map((employee) => toEmployeePaycheck(employee, byId.get(employee.id))));
        // Straight passthrough of the server's own totals -- no client-side
        // sum, same rule as every paycheck figure above it.
        setTotals(run.totals);
      })
      .catch((err: unknown) => {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        // A request-level failure (network down, empty roster, malformed body)
        // rejects the whole run rather than any one row -- every employee gets
        // the same honest, server-provided reason instead of a fixture-like
        // number as if nothing went wrong.
        const message =
          err instanceof PayrollValidationError
            ? // A PayrollValidationError's message is the server's own reason — "hours must
              // be positive" and the like — which is exactly what the reader needs. Kept.
              err.message
            : err instanceof ApiError
              ? // An ApiError's message is NOT: `${status} ${statusText}: ${body}` at every
                // throw site in api/client.ts. See lib/apiFailure.ts for the 502 that
                // reached the Live Wall's live region reading "502 Bad Gateway".
                describeReadFailure(err)
              : "Could not reach the payroll engine.";
        setRows(
          employees.map((employee) => ({ employee, status: "error", paycheck: null, error: message })),
        );
        setTotals(null);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `employees` IS a real dependency now that the roster is fetched
    // (usePayrollRoster) rather than imported as a module-level constant: its identity
    // changes exactly once, when the roster arrives, and that is precisely when the run
    // must fire. The hook holds the array in state and never rebuilds it on a re-render,
    // so this does not re-fire on every render the way a fresh array literal would.
  }, [employees]);

  const readyCount = rows.filter((r) => r.status === "ready" && r.paycheck !== null).length;
  const erroredCount = rows.filter((r) => r.status === "error").length;
  // A roster that has not arrived is still "loading" even though there are no rows to be
  // loading -- otherwise a screen would read an un-fetched roster as a finished run of
  // nobody. An EMPTY roster (`[]`) is a finished answer and reports ready.
  const status: PayrollRunStatus =
    employees === null || rows.some((r) => r.status === "loading") ? "loading" : "ready";

  return { rows, status, totals, readyCount, erroredCount };
}
